/**
 * InMemoryStorage acts as a drop-in replacement of Web Storage APIs such as
 * window.localStorage or window.sessionStorage.
 *
 * Heavily inspired by: https://github.com/Download/memorystorage/blob/master/src/memorystorage.js
 */
export class InMemoryStorage implements Storage {
  // eslint-disable-next-line no-restricted-globals
  [name: string]: any;

  get length(): number {
    return this.enumerableKeys().length;
  }

  private _cloak: { [key: string]: any } = {};

  constructor(defaultValue?: { [name: string]: any }) {
    if (defaultValue) {
      Object.keys(defaultValue).forEach(key => {
        if (key in storageCloak) {
          this._cloak[key] = defaultValue[key];
        } else {
          this[key] = defaultValue[key];
        }
      });
    }
  }

  clear(): void {
    let keys = this.uncloakedKeys();

    for (let i = 0, key; (key = keys[i]); i++) {
      delete this[key];
    }

    keys = this.cloakedKeys();

    for (let i = 0, key; (key = keys[i]); i++) {
      delete this._cloak[key];
    }
  }

  getItem(key: string): string | null {
    return (key in storageCloak ? this._cloak[key] : this[key]) || null;
  }

  key(index: number): string | null {
    const keys = this.enumerableKeys();

    return index >= 0 && index < keys.length ? keys[index] : null;
  }

  removeItem(key: string): void {
    key in storageCloak ? delete this._cloak[key] : delete this[key];
  }

  setItem(key: string, value: string): void {
    this[key] = value;
  }

  private uncloakedKeys() {
    return Object.keys(this).filter(x => {
      return !(x in storageCloak || x === '_cloak');
    });
  }

  private cloakedKeys() {
    return Object.keys(this._cloak);
  }

  private enumerableKeys() {
    return this.uncloakedKeys().concat(this.cloakedKeys());
  }
}

const storageCloak: Record<
  keyof Pick<Storage, 'clear' | 'getItem' | 'key' | 'length' | 'removeItem' | 'setItem'>,
  boolean
> = {
  clear: true,
  getItem: true,
  key: true,
  length: true,
  removeItem: true,
  setItem: true,
};

export const memoryStorage = new InMemoryStorage();
